import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { RecordingsPortalPage } from '@/personas/portal/RecordingsPortalPage';
import { LoginPage } from '@/personas/LoginPage';
import { SessionReplacedNotice } from '@/components/SessionReplacedNotice';
import { AgentLayout } from '@/personas/agent/AgentLayout';
import { Softphone } from '@/personas/agent/pages/Softphone';
import { ActiveCall } from '@/personas/agent/pages/ActiveCall';
import { Dialer } from '@/personas/agent/pages/Dialer';
import { CampaignWorkspace } from '@/personas/agent/pages/CampaignWorkspace';
import { Callbacks } from '@/personas/agent/pages/Callbacks';
import { MyLeads } from '@/personas/agent/pages/MyLeads';
import { MyPerformance } from '@/personas/agent/pages/MyPerformance';
import { MyCampaigns } from '@/personas/agent/pages/MyCampaigns';
import { LeadSearch } from '@/personas/agent/pages/LeadSearch';
import { History as AgentHistory } from '@/personas/agent/pages/History';
import { Contacts } from '@/personas/agent/pages/Contacts';
import { VoicemailPage } from '@/personas/agent/pages/VoicemailPage';
import { Tasks } from '@/personas/agent/pages/Tasks';
import { Tickets as AgentTickets } from '@/personas/agent/pages/Tickets';
import { Shifts } from '@/personas/agent/pages/Shifts';
import { Settings as AgentSettings } from '@/personas/agent/pages/Settings';
import { Help } from '@/personas/agent/pages/Help';

import { AdminLayout } from '@/personas/admin/AdminLayout';
import { AdminDashboard } from '@/personas/admin/pages/Dashboard';
import { Insights } from '@/personas/admin/pages/Insights';
import { LiveMonitor } from '@/personas/admin/pages/LiveMonitor';
import { TelephonyCenter } from '@/personas/admin/pages/TelephonyCenter';
import { Campaigns } from '@/personas/admin/pages/Campaigns';
import { AdminLogs } from '@/personas/admin/pages/Logs';
import { Onboarding } from '@/personas/admin/pages/Onboarding';
import { AdminSettings } from '@/personas/admin/pages/Settings';
import { Billing } from '@/personas/admin/pages/Billing';
import { LeadsContacts } from '@/personas/admin/pages/LeadsContacts';
import { CallsCenter } from '@/personas/admin/pages/CallsCenter';
import { Cdr } from '@/personas/admin/pages/Cdr';
import { Dids } from '@/personas/admin/pages/Dids';
import { Reports as AdminReports } from '@/personas/admin/pages/Reports';

import { SuperAdminLayout } from '@/personas/superadmin/SuperAdminLayout';
import { SuperDashboard } from '@/personas/superadmin/pages/SuperDashboard';
import { Tenants } from '@/personas/superadmin/pages/Tenants';
import { Plans } from '@/personas/superadmin/pages/Plans';
import { Invoices } from '@/personas/superadmin/pages/Invoices';
import { SuperBilling } from '@/personas/superadmin/pages/SuperBilling';
import { SuperCosts } from '@/personas/superadmin/pages/SuperCosts';
import { Trunks } from '@/personas/superadmin/pages/Trunks';
import { VoiceCenter } from '@/personas/superadmin/pages/VoiceCenter';
import { DidPool } from '@/personas/superadmin/pages/DidPool';
import { Audit } from '@/personas/superadmin/pages/Audit';
import { Tickets } from '@/personas/superadmin/pages/Tickets';
import { Health } from '@/personas/superadmin/pages/Health';
import { WhiteLabel } from '@/personas/superadmin/pages/WhiteLabel';
import { InternalUsers } from '@/personas/superadmin/pages/InternalUsers';
import { SuperNotifications } from '@/personas/superadmin/pages/SuperNotifications';
import { SuperReports } from '@/personas/superadmin/pages/SuperReports';
import { SuperApi } from '@/personas/superadmin/pages/SuperApi';
import { McpSettings } from '@/personas/superadmin/pages/McpSettings';

import { ProtectedRoute } from '@/routes/ProtectedRoute';
import { useAuthStore } from '@/shared/stores/auth';
import { useThemeStore, applyThemeToHtml } from '@/shared/stores/theme';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false },
  },
});

function ThemeApplier() {
  const variation = useThemeStore((s) => s.variation);
  const density = useThemeStore((s) => s.density);
  const theme = useThemeStore((s) => s.theme);

  useEffect(() => {
    applyThemeToHtml({ variation, density, theme });
  }, [variation, density, theme]);

  return null;
}

function HomeRedirect() {
  const persona = useAuthStore((s) => s.persona);
  const token = useAuthStore((s) => s.token);

  if (!token || !persona) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={`/${persona}`} replace />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeApplier />
      <SessionReplacedNotice />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/login" element={<LoginPage />} />

          {/* פורטל ההקלטות ללקוח — ציבורי במכוון, בלי ProtectedRoute.
              הסלאג בנתיב הוא מה שתוחם את החיפוש לעסק אחד. */}
          <Route path="/p/:tenantSlug/recordings" element={<RecordingsPortalPage />} />

          <Route
            path="/agent"
            element={
              <ProtectedRoute persona="agent">
                <AgentLayout />
              </ProtectedRoute>
            }
          >
            {/* 08/09/2026: הנציג נוחת על הקמפיינים שלו (קודם: שיחות מעקב). */}
            <Route index element={<Navigate to="/agent/campaigns" replace />} />
            <Route path="campaigns" element={<MyCampaigns />} />
            <Route path="my-leads" element={<MyLeads />} />
            <Route path="search" element={<LeadSearch />} />
            {/* הסופטפון כדף נשאר נגיש בנתיב מפורש (מוסתר מהתפריט כברירת מחדל). */}
            <Route path="softphone" element={<Softphone />} />
            <Route path="active" element={<ActiveCall />} />
            <Route path="dialer" element={<Dialer />} />
            {/* מסך העבודה החדש על קמפיין (שלושה פאנלים). נבנה לצד החייגן
                הישן ולא במקומו — הישן ממשיך לעבוד ב-/agent/dialer. */}
            <Route path="campaign" element={<CampaignWorkspace />} />
            <Route path="callbacks" element={<Callbacks />} />
            <Route path="history" element={<AgentHistory />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="voicemail" element={<VoicemailPage />} />
            {/* תסריטים עברו ללשונית בהגדרות; סטטיסטיקה והתראות בוטלו (הפעמון הוא הבית של ההתראות) */}
            <Route path="scripts" element={<Navigate to="/agent/settings?tab=scripts" replace />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="performance" element={<MyPerformance />} />
            <Route path="stats" element={<Navigate to="/agent/performance" replace />} />
            <Route path="tickets" element={<AgentTickets />} />
            <Route path="shifts" element={<Shifts />} />
            <Route path="notifications" element={<Navigate to="/agent" replace />} />
            <Route path="settings" element={<AgentSettings />} />
            <Route path="help" element={<Help />} />
          </Route>

          <Route
            path="/admin"
            element={
              <ProtectedRoute persona="admin">
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="insights" element={<Insights />} />
            <Route path="live" element={<LiveMonitor />} />
            {/* אותו מסך עבודה של הנציג, נגיש גם למנהל: "חייג סבב" בכרטיס
                הקמפיין מצרף אותו כנציג ומחייג, ולכן הוא צריך לנחות על מסך
                שבו רואים את הליד. ה-persona שלו הוא 'admin', ולכן הנתיב
                תחת /agent היה מחזיר אותו החוצה. */}
            <Route path="campaign" element={<CampaignWorkspace />} />
            {/* 07/09/2026 — תפריט מסודר אחד: נציגים/תיוג בהגדרות, תא קולי בשיחות,
                CRM בלידים, IVR/תורים/חסומים/סוכן קולי בטלפוניה. הישנים מפנים. */}
            <Route path="agents" element={<Navigate to="/admin/settings?tab=staff" replace />} />
            <Route path="teams" element={<Navigate to="/admin/settings?tab=staff&sub=teams" replace />} />
            <Route path="roles" element={<Navigate to="/admin/settings?tab=staff&sub=roles" replace />} />
            <Route path="queues" element={<Navigate to="/admin/telephony?tab=queues" replace />} />
            <Route path="ivr" element={<Navigate to="/admin/telephony" replace />} />
            <Route path="extensions" element={<Navigate to="/admin/settings?tab=telephony" replace />} />
            <Route path="dids" element={<Dids />} />
            <Route path="telephony" element={<TelephonyCenter />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="leads" element={<LeadsContacts />} />
            <Route path="scripts" element={<Navigate to="/admin/settings?tab=scripts" replace />} />
            <Route path="dispositions" element={<Navigate to="/admin/settings?tab=tagging" replace />} />
            <Route path="call-tags" element={<Navigate to="/admin/settings?tab=tagging&sub=tags" replace />} />
            <Route path="voicemail" element={<Navigate to="/admin/cdr?tab=voicemail" replace />} />
            <Route path="crm" element={<Navigate to="/admin/leads?tab=contacts" replace />} />
            <Route path="cdr" element={<CallsCenter />} />
            <Route path="recordings" element={<Cdr recordedOnly />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="logs" element={<AdminLogs />} />
            <Route path="voice-agents" element={<Navigate to="/admin/telephony" replace />} />
            <Route path="blocked-numbers" element={<Navigate to="/admin/telephony?tab=blocked" replace />} />
            <Route path="billing" element={<Billing />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="onboarding" element={<Onboarding />} />
          </Route>

          <Route
            path="/super"
            element={
              <ProtectedRoute persona="super">
                <SuperAdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<SuperDashboard />} />
            <Route path="health" element={<Health />} />
            <Route path="notifications" element={<SuperNotifications />} />
            <Route path="reports" element={<SuperReports />} />
            <Route path="tenants" element={<Tenants />} />
            <Route path="internal-users" element={<InternalUsers />} />
            <Route path="plans" element={<Plans />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="billing" element={<SuperBilling />} />
            <Route path="costs" element={<SuperCosts />} />
            <Route path="api" element={<SuperApi />} />
            <Route path="trunks" element={<Trunks />} />
            <Route path="voice-agents" element={<VoiceCenter />} />
            <Route path="voice-tools" element={<Navigate to="/super/voice-agents?tab=tools" replace />} />
            <Route path="voice-flows" element={<Navigate to="/super/voice-agents?tab=flows" replace />} />
            <Route path="did-pool" element={<DidPool />} />
            <Route path="mcp" element={<McpSettings />} />
            <Route path="audit" element={<Audit />} />
            <Route path="tickets" element={<Tickets />} />
            <Route path="whitelabel" element={<WhiteLabel />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
